"use client";

import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface FooterDotsProps {
  className?: string;
}

/**
 * A field of slowly drifting dots for the footer background — the moving-dots
 * replacement for the old static contour lines, echoing the site's dot-matrix
 * motif. Decorative (aria-hidden). The loop pauses when the tab is hidden
 * (requestAnimationFrame) and stays static under prefers-reduced-motion.
 */
export function FooterDots({ className }: FooterDotsProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    const css = getComputedStyle(document.documentElement);
    const warm = css.getPropertyValue("--color-parchment").trim() || "#f3eee3";
    const brass = css.getPropertyValue("--color-secondary-light").trim() || "#c89a4e";

    let W = 0;
    let H = 0;
    let raf = 0;

    interface Dot {
      x: number;
      y: number;
      vx: number;
      vy: number;
      r: number;
      a: number;
      brass: boolean;
    }
    let dots: Dot[] = [];
    const rnd = (a: number, b: number) => a + Math.random() * (b - a);

    function build() {
      const w = wrap!.clientWidth;
      const h = wrap!.clientHeight;
      W = Math.round(w * DPR);
      H = Math.round(h * DPR);
      canvas!.width = W;
      canvas!.height = H;
      canvas!.style.width = `${w}px`;
      canvas!.style.height = `${h}px`;
      const N = Math.max(24, Math.min(140, Math.round((w * h) / 9000)));
      dots = Array.from({ length: N }, () => {
        const speed = reduced ? 0 : rnd(0.05, 0.22) * DPR;
        const ang = rnd(0, Math.PI * 2);
        return {
          x: Math.random() * W,
          y: Math.random() * H,
          vx: Math.cos(ang) * speed,
          vy: Math.sin(ang) * speed,
          r: rnd(1.0, 1.9) * DPR,
          a: rnd(0.08, 0.3),
          brass: Math.random() < 0.14,
        };
      });
    }

    function draw() {
      if (!ctx) return;
      ctx.clearRect(0, 0, W, H);
      for (const d of dots) {
        if (!reduced) {
          d.x += d.vx;
          d.y += d.vy;
          if (d.x < -2) d.x = W + 2;
          else if (d.x > W + 2) d.x = -2;
          if (d.y < -2) d.y = H + 2;
          else if (d.y > H + 2) d.y = -2;
        }
        ctx.globalAlpha = d.a;
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = d.brass ? brass : warm;
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      if (!reduced) raf = requestAnimationFrame(draw);
    }

    function start() {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(draw);
    }

    build();
    draw(); // initial paint; also kicks off the loop unless reduced-motion

    // requestAnimationFrame stops while the tab is hidden; resume on return.
    const onVis = () => {
      if (!document.hidden && !reduced) start();
    };
    document.addEventListener("visibilitychange", onVis);

    let rz = 0;
    const ro = new ResizeObserver(() => {
      window.clearTimeout(rz);
      rz = window.setTimeout(() => {
        build();
        if (reduced) draw();
      }, 200);
    });
    ro.observe(wrap);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.clearTimeout(rz);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return (
    <div ref={wrapRef} aria-hidden="true" className={cn("pointer-events-none", className)}>
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
}
